import * as React from 'react';
import classnames from 'classnames';
import { ICoreContext } from '@msdyn365-commerce/core';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { humanizeUnitOfMeasure } from '../unit-of-measure';
import { IAppSettingsInContext } from '../../settings/app.settings.autogenerated';

export interface IQuantityInputProps {
    mode?: 'edit' | 'view';
    context: ICoreContext<IAppSettingsInContext>;
    disable?: boolean;
    product: SimpleProduct | undefined;
    quantity: number;
    breakoff: boolean;
    nonBreakoffUOM?: string;
    onQuantityChange: (quantity: number, applyImmediately: boolean) => void;
    BuyButton?: React.ReactNode;
    allowZero?: boolean;
}

const onlyDigitsAndDecimalRegex = /[0-9.,]/;
const BeforeInputRules = [
    // Only allow digits and decimal point
    (str: string) => onlyDigitsAndDecimalRegex.test(str),
    // Only allow one leading zero before decimal point (or if no decimal point)
    (str: string) => str.startsWith('00') === false,
    // Only allow one decimal character
    (str: string) => [...str].filter(c => c === '.' || c === ',').length <= 1,
    // Only allow up to two decimal places
    (str: string) => str.indexOf('.') === -1 || str.split('.')[1]?.length <= 2
];

function isDecimalAllowed(product: SimpleProduct | undefined, breakoff: boolean): boolean {
    // Only relevant for kg products
    if (!product || product.DefaultUnitOfMeasure !== 'kg') {
        return false;
    }

    const multipleOfQuantity = product.Behavior?.MultipleOfQuantity;

    // If the product has breakoff available only allow if breakoff is enabled
    if (multipleOfQuantity && multipleOfQuantity > 1) {
        return breakoff;
    }

    // Otherwise always allow decimal
    return true;
}

const QuantityInput = (props: IQuantityInputProps) => {
    const {
        mode = 'edit',
        context,
        disable,
        product,
        quantity,
        breakoff,
        nonBreakoffUOM,
        onQuantityChange: onQuantityChangeExternal,
        allowZero
    } = props;

    const inputRef = React.useRef<HTMLInputElement>(null);

    const allowDecimal = isDecimalAllowed(product, breakoff);
    const allowBelowOne = allowDecimal &&
        product?.DefaultUnitOfMeasure === 'kg' &&
        context.app.config?.allowFractionalQuantityForKgProductsInOUNs
            ?.split(',')
            .map(uom => uom.trim().toLowerCase())
            .includes(context.request.apiSettings.oun.toLowerCase());

    const onQuantityChange = (newQuantity: number, applyImmediately = false) => {
        if (quantity !== newQuantity || applyImmediately) {
            onQuantityChangeExternal(newQuantity, applyImmediately);
        }

        // Update the input value directly to avoid lag
        if (inputRef.current) {
            const multiplier = breakoff ? 1 : product?.Behavior?.MultipleOfQuantity || 1;
            const displayQuantity = parseFloat((newQuantity / multiplier).toFixed(2));

            // Preserve user entered decimals by only changing the value if it's different number wise
            if (parseFloat(inputRef.current.value) === displayQuantity) {
                return;
            }

            inputRef.current.value = displayQuantity.toString();
        }
    };

    React.useEffect(() => {
        if (!product || allowZero || quantity > 0) {
            return;
        }
        // Initial setup for quantity
        const multiplier = breakoff ? 1 : product.Behavior?.MultipleOfQuantity || 1;
        onQuantityChange(validateQuantity(1 * multiplier));
    }, [product]);

    React.useEffect(() => {
        if (!product) {
            return;
        }
        // Quantity was changed externally, update the input value
        const multiplier = breakoff ? 1 : product.Behavior?.MultipleOfQuantity || 1;
        const minimumQuantity = allowZero
            ? 0
            : (allowBelowOne && quantity > 0 ? 0.01 : 1) * multiplier;
        onQuantityChange(Math.max(minimumQuantity, quantity), false);
    }, [quantity]);

    const baseClassName = 'quantity-input';
    // Check if there is a friendly UOM defined on the SimpleProduct
    const friendlyUOM = product?.ExtensionProperties?.find((x) => x.Key === 'FRIENDLYUOM')?.Value?.StringValue;
    // @ts-expect-error
    const breakoffUOM = humanizeUnitOfMeasure(context, friendlyUOM || product?.DefaultUnitOfMeasure, product);
    const uom = breakoff || !nonBreakoffUOM
        ? humanizeUnitOfMeasure(context, friendlyUOM || product?.DefaultUnitOfMeasure, product)
        : humanizeUnitOfMeasure(context, nonBreakoffUOM);

    // const selectedUOM = breakoff ? uom : 'KASSE(R)';
    const multiplier = breakoff ? 1 : product?.Behavior?.MultipleOfQuantity || 1;

    // const minimumQuantity = +!allowZero * multiplier;
    const minimumQuantity = allowZero
        ? 0
        : (allowBelowOne ? 0.01 : 1) * multiplier;

    const validateQuantity = (quantity: number): number => {
        const newQuantity = Math.max(minimumQuantity, quantity);
        return Math.round(newQuantity * 100) / 100; // Round to two decimal places
    };

    const onIncrement = () => {
        const newQuantity = validateQuantity(quantity + multiplier);
        onQuantityChange(newQuantity);
    };

    const onDecrement = () => {
        const newQuantity = validateQuantity(Math.max(0, quantity - multiplier));
        onQuantityChange(newQuantity);
    };

    const changeInputValue = (value: string, offsetStart: number = 0) => {
        if (inputRef.current && inputRef.current.value !== value) {
            const selectionStart = inputRef.current.selectionStart || 0;
            const selectionEnd = inputRef.current.selectionEnd || 0;

            // Set the value
            inputRef.current.value = value;

            // Restore the cursor position
            inputRef.current.setSelectionRange(selectionStart + offsetStart, selectionEnd + offsetStart);
        }
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>, validate: boolean = false, applyImmediately = false) => {
        let rawValue = e.target.value;
        let offsetStart = 0;

        if (rawValue.includes(',')) {
            // Replace comma with dot and update input value
            rawValue = rawValue.replace(',', '.');
        }

        if (rawValue.startsWith('.')) {
            rawValue = `0${rawValue}`;
            offsetStart = 1;
        }

        // Normalize the input value
        changeInputValue(rawValue, offsetStart);

        const parsedValue = Math.min(9999, parseFloat(rawValue) || 0);

        // Do nothing if it looks like it's still being typed
        if (!validate && (rawValue === '' || parsedValue === 0 || rawValue.endsWith('.') || rawValue.startsWith('.'))) {
            return;
        }

        // Remove trailing dot
        if (rawValue.endsWith('.')) {
            changeInputValue(rawValue.slice(0, -1));
        }

        // We're essentially changing the displayQuantity instead of the actual quantity
        const newQuantity = breakoff
            ? validateQuantity(parsedValue)
            : validateQuantity(parsedValue * multiplier);

        onQuantityChange(newQuantity, applyImmediately);
    };

    const initialQuantity = product && parseFloat((quantity / multiplier).toFixed(2));
    const isM1ProductAndHasBreakoff = product?.DefaultUnitOfMeasure?.endsWith('-M1') && product.Behavior?.MultipleOfQuantity && product.Behavior.MultipleOfQuantity > 1;

    return (
        <div className={baseClassName}>
            <div className={`${baseClassName}__quantity-container`}>
                <div className={`${baseClassName}__quantity-controls`}>
                    {mode === 'edit' && (
                        <button
                            className={`${baseClassName}__quantity-controls__minus`}
                            disabled={disable || !product || quantity === minimumQuantity}
                            onClick={onDecrement}
                        >
                            -
                        </button>
                    )}
                    <input
                        ref={inputRef}
                        className={classnames(`${baseClassName}__quantity-controls__input`, mode)}
                        readOnly={mode !== 'edit' || disable}
                        /* disabled={disable} */
                        defaultValue={initialQuantity?.toString()}
                        min={minimumQuantity}
                        onBeforeInput={(e) => {
                            e.persist();

                            if (!inputRef.current) {
                                return;
                            }

                            const data = (e.nativeEvent as InputEvent).data;
                            const { selectionStart, selectionEnd, value } = inputRef.current;
                            const proposedValue = value.substring(0, selectionStart || 0) +
                                (data || '') +
                                value.substring(selectionEnd || 0);

                            if (proposedValue && !BeforeInputRules.every(rule => rule(proposedValue))) {
                                e.preventDefault();
                                return;
                            }
                        }}
                        onKeyPress={(e) => {
                            e.persist();

                            if (!inputRef.current) {
                                return;
                            }

                            // Only allow numbers and decimal point (if allowed)
                            if ((!allowDecimal && (e.key === '.' || e.key === ',')) || !onlyDigitsAndDecimalRegex.test(e.key)) {
                                e.preventDefault();
                                return;
                            }
                        }}
                        onChange={onInputChange}
                        onBlur={(e) => {
                            // If the input is empty, set it to 0
                            if (e.target.value === '') {
                                onInputChange({ target: { value: '0' } } as React.ChangeEvent<HTMLInputElement>, true, true);
                            } else {
                                onInputChange(e, true, true);
                            }
                        }}
                        data-nomodalcss />
                    <span className={classnames(`${baseClassName}__quantity-controls__uom`, { disabled: disable })} onClick={(e) => {
                        // Set focus to input above
                        const input = (e.target as HTMLElement).previousElementSibling as HTMLInputElement;
                        if (input) {
                            input.focus();
                        }
                    }}>
                        {!!isM1ProductAndHasBreakoff && `x ${product!.Behavior!.MultipleOfQuantity} `}
                        {uom}
                    </span>
                    {mode === 'edit' && (
                        <button
                            className={`${baseClassName}__quantity-controls__plus`}
                            disabled={disable || !product}
                            onClick={onIncrement}
                        >
                            +
                        </button>
                    )}
                </div>

                <div className={`${baseClassName}__buy-container`}>
                    {props.BuyButton}
                </div>
            </div>
        </div>
    );
};

export default QuantityInput;
